/**
 * Retries for /start's reads (GETs) when the API is busy. Kong limits each client to a few
 * requests a second per API and answers 429 past it, and an API shedding load answers 503. A read
 * that hears either waits as long as Retry-After says (never more than 3 s) or, when the header
 * isn't there or can't be read, about 0.5 s and then 1.5 s, give or take a fifth so that tabs
 * don't retry in step. It's tried at most twice more; then the step shows its own error.
 *
 * Writes (preparing the subscription, booking, the account codes) never come through here: a
 * write that may have reached the server is never sent again on its own.
 *
 * Part of every page's first load (startPrefetch.js uses it), so it stays small.
 */
export const THROTTLED_RETRIES = 2

const BACKOFF_MS = [500, 1500]
const RETRY_AFTER_CAP_MS = 3000

export const isThrottled = (error) => error?.status === 429 || error?.status === 503

/** Retry-After in milliseconds, from delta-seconds or an HTTP date; null without one. */
export const parseRetryAfter = (value, now = Date.now()) => {
  if (value === null || value === undefined || String(value).trim() === '') return null
  const seconds = Number(value)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const date = Date.parse(value)
  return Number.isNaN(date) ? null : Math.max(0, date - now)
}

/** The wait before retry `attempt` (0 for the first) of a throttled read. */
export const throttledDelay = (attempt, error, random = Math.random) => {
  if (typeof error?.retryAfterMs === 'number') return Math.min(error.retryAfterMs, RETRY_AFTER_CAP_MS)
  const base = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]
  return Math.round(base * (0.8 + random() * 0.4))
}

// react-query's own delay, for the reads whose rule doesn't name one.
const defaultDelay = (attempt) => Math.min(1000 * 2 ** attempt, 30000)

/**
 * A read query's `retry` and `retryDelay`: a throttled answer as above, anything else by the
 * query's own rule (`retry` a count or a function; `retryDelay` a function, or react-query's
 * default when absent).
 */
export const readRetry = ({ retry = 0, retryDelay = defaultDelay } = {}) => ({
  retry: (count, error) => {
    if (isThrottled(error)) return count < THROTTLED_RETRIES
    return typeof retry === 'function' ? retry(count, error) : count < retry
  },
  retryDelay: (count, error) => (isThrottled(error) ? throttledDelay(count, error) : retryDelay(count, error))
})
